import React, { useState, useEffect, useCallback, memo } from 'react';
import { api } from '../../api';
import Select from '../../components/Select';
import Field, { FormRow } from '../../components/ui/Field';
import Estado from '../../components/ui/Estado';
import { agora } from '../../utils/format';
import { UNIDADES_PRODUCAO, descreverArea, labelUnidade } from '../../utils/unidades';

const TIPOS_CLASSIFICACAO = [
  { value: 'PH', label: 'PH (peso hectolítrico)' },
  { value: 'GRAU', label: 'Grau (brix/açúcar)' },
  { value: 'CLASSE', label: 'Classe (1, 2, 3, 4, 5)' },
  { value: 'TIPO', label: 'Tipo (1, 2, 3)' },
  { value: 'CATEGORIA', label: 'Categoria (Extra, I, II)' },
  { value: 'PENEIRA', label: 'Peneira' },
  { value: 'OUTRO', label: 'Outro' },
];

export const FORM_CARGA_VAZIO = {
  safraId: '', safraLavouraId: '', armazemId: '',
  placaCaminhao: '', pesoBrutoKg: '', pesoLiquidoKg: '', taraKg: '',
  dataHoraEntrega: '', depositadoEm: '', notaEmitida: false,
  unidadeProducao: 'KG', quantidade: '', destino: '',
  tipoClassificacao: '', classificacao: '',
  umidadePct: '', impurezaPct: '',
};

/**
 * Formulário de registro de produção.
 *
 * Os campos essenciais (quando, onde, quanto) ficam visíveis e o resto entra num
 * bloco recolhível: quem registra dez cargas por dia só precisa dos três.
 */
const FormCarga = memo(function FormCarga({
  armazens, safras, usuarios, initial, onSave, onCancel, onSucesso, submitLabel, embutido,
}) {
  const [safraLavouras, setSafraLavouras] = useState([]);
  const [carregandoLavouras, setCarregandoLavouras] = useState(false);
  const [erroLavouras, setErroLavouras] = useState(null);
  const [form, setForm] = useState(initial);
  const [msg, setMsg] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [mostrarOpcional, setMostrarOpcional] = useState(!!initial.id);

  const set = (campo) => (v) => setForm(f => ({ ...f, [campo]: v }));
  const setEv = (campo) => (e) => setForm(f => ({ ...f, [campo]: e.target.value }));

  /**
   * Áreas plantadas da safra escolhida.
   *
   * A falha aqui era engolida: o formulário dizia "esta safra ainda não tem área
   * plantada" quando na verdade a chamada não completou — em plena colheita, com
   * as áreas cadastradas. Agora o erro aparece e dá para tentar de novo.
   */
  const carregarLavouras = useCallback(() => {
    if (!form.safraId) {
      setSafraLavouras([]);
      setErroLavouras(null);
      setCarregandoLavouras(false);
      return undefined;
    }
    let vivo = true;
    setCarregandoLavouras(true);
    setErroLavouras(null);
    api.getLavourasAtivasDaSafra(form.safraId)
      .then(l => { if (vivo) setSafraLavouras(l || []); })
      .catch(() => {
        if (!vivo) return;
        setSafraLavouras([]);
        setErroLavouras('Não foi possível carregar as áreas plantadas desta safra. Verifique sua conexão e tente novamente.');
      })
      .finally(() => { if (vivo) setCarregandoLavouras(false); });
    return () => { vivo = false; };
  }, [form.safraId]);

  useEffect(() => carregarLavouras(), [carregarLavouras]);

  // KG é a única unidade com balança: nas outras não há bruto/tara/líquido.
  const porPeso = !form.unidadeProducao || form.unidadeProducao === 'KG';
  const unidade = form.unidadeProducao || 'KG';

  // Preenche o líquido sozinho quando bruto e tara estão informados: é a conta
  // que o usuário fazia na calculadora antes de digitar.
  const liquidoCalculado = porPeso && form.pesoBrutoKg && form.taraKg
    ? (parseFloat(form.pesoBrutoKg) - parseFloat(form.taraKg))
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);
    // Select não bloqueia submit pelo navegador: sem estes avisos o botão ficava
    // sem resposta quando faltava safra ou área.
    if (!form.safraId) { setMsg({ type: 'error', text: 'Escolha a safra.' }); return; }
    if (!form.safraLavouraId) { setMsg({ type: 'error', text: 'Escolha a área de cultivo.' }); return; }
    setSalvando(true);
    try {
      await onSave({
        armazemId: form.armazemId ? parseInt(form.armazemId) : null,
        safraLavouraId: parseInt(form.safraLavouraId),
        placaCaminhao: form.placaCaminhao || null,
        pesoBrutoKg: form.pesoBrutoKg ? parseFloat(form.pesoBrutoKg) : null,
        pesoLiquidoKg: form.pesoLiquidoKg ? parseFloat(form.pesoLiquidoKg) : null,
        taraKg: form.taraKg ? parseFloat(form.taraKg) : null,
        dataHoraEntrega: form.dataHoraEntrega ? form.dataHoraEntrega + ':00' : null,
        depositadoEm: form.depositadoEm || null,
        notaEmitida: form.notaEmitida || false,
        unidadeProducao: unidade,
        quantidade: form.quantidade
          ? parseFloat(form.quantidade)
          : (form.pesoLiquidoKg ? parseFloat(form.pesoLiquidoKg) : null),
        destino: form.destino || null,
        tipoClassificacao: form.tipoClassificacao || null,
        classificacao: form.classificacao || null,
        umidadePct: form.umidadePct ? parseFloat(form.umidadePct) : null,
        impurezaPct: form.impurezaPct ? parseFloat(form.impurezaPct) : null,
      });
      if (onSucesso) {
        onSucesso();
        return;
      }
      setMsg({ type: 'success', text: 'Salvo com sucesso!' });
      if (!initial.id) {
        setForm({ ...initial, dataHoraEntrega: agora() });
        setMostrarOpcional(false);
      }
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className={embutido ? undefined : 'card'}>
      {msg && <div className={`msg msg-${msg.type}`} role="status">{msg.text}</div>}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        <FormRow>
          <Field label="Data/Hora" obrigatorio>
            <input
              type="datetime-local"
              value={form.dataHoraEntrega}
              onChange={setEv('dataHoraEntrega')}
              required
            />
          </Field>
          <Field label="Safra / Ciclo" obrigatorio>
            <Select
              value={form.safraId}
              onChange={v => setForm(f => ({ ...f, safraId: v, safraLavouraId: '' }))}
              options={safras
                .filter(s => s.status !== 'FECHADA')
                .map(s => ({ value: s.id, label: `${s.cultura || 'Safra'} — ${s.descricao}` }))}
              placeholder="Selecione"
              required
            />
          </Field>
        </FormRow>

        {/* Buscando as áreas, falha na busca e safra realmente sem área plantada
            são três coisas diferentes — só a última manda cadastrar em Safras. */}
        <Estado erro={erroLavouras} aoTentarNovamente={carregarLavouras}>
          <FormRow>
            <Field label="Área de Cultivo" largura="cheia" obrigatorio
              hint={form.safraId && !carregandoLavouras && safraLavouras.length === 0
                ? 'Esta safra ainda não tem área plantada. Cadastre em Safras / Ciclos.'
                : undefined}>
              <Select
                value={form.safraLavouraId}
                onChange={set('safraLavouraId')}
                options={safraLavouras.map(sl => ({
                  value: sl.id,
                  label: descreverArea(sl.lavoura, sl.areaHectares),
                }))}
                placeholder={!form.safraId
                  ? 'Selecione uma safra primeiro'
                  : carregandoLavouras ? 'Buscando áreas...' : 'Selecione'}
                required
                disabled={!form.safraId || carregandoLavouras}
              />
            </Field>
          </FormRow>
        </Estado>

        <FormRow min={150}>
          <Field label="Unidade" hint="Como você mediu esta colheita">
            <Select value={unidade} onChange={set('unidadeProducao')} options={UNIDADES_PRODUCAO} />
          </Field>
          {porPeso ? (
            <Field label="Peso Líquido (kg)" obrigatorio
              hint={liquidoCalculado != null && !form.pesoLiquidoKg
                ? `Bruto − tara = ${liquidoCalculado.toLocaleString('pt-BR')} kg`
                : undefined}>
              <input
                type="number" step="0.01" min="0"
                value={form.pesoLiquidoKg || ''}
                onChange={setEv('pesoLiquidoKg')}
                placeholder="Ex: 60000"
                required
              />
            </Field>
          ) : (
            <Field label={`Quantidade (${labelUnidade(unidade)})`} obrigatorio>
              <input
                type="number" step="0.01" min="0"
                value={form.quantidade || ''}
                onChange={setEv('quantidade')}
                placeholder="Ex: 50"
                required
              />
            </Field>
          )}
        </FormRow>

        <button
          type="button"
          className="mais-campos"
          onClick={() => setMostrarOpcional(v => !v)}
          aria-expanded={mostrarOpcional}
        >
          {mostrarOpcional
            ? '▲ Ocultar campos opcionais'
            : '▼ Mais campos (armazém, pesos, classificação...)'}
        </button>

        {mostrarOpcional && (
          <div className="bloco-opcional">
            <FormRow>
              <Field label="Armazém" largura="cheia" hint="Onde a carga foi depositada">
                <Select
                  value={form.armazemId}
                  onChange={set('armazemId')}
                  options={armazens.map(a => ({ value: a.id, label: `${a.nome} — ${a.cidade}` }))}
                  placeholder="Selecione (opcional)"
                />
              </Field>
            </FormRow>

            {porPeso && (
              <FormRow min={140}>
                <Field label="Peso Bruto (kg)">
                  <input type="number" step="0.01" min="0" value={form.pesoBrutoKg} onChange={setEv('pesoBrutoKg')} />
                </Field>
                <Field label="Tara (kg)" hint="Peso do caminhão vazio">
                  <input type="number" step="0.01" min="0" value={form.taraKg} onChange={setEv('taraKg')} />
                </Field>
              </FormRow>
            )}

            <FormRow>
              <Field label="Depositado em nome de" largura="cheia">
                <Select
                  value={form.depositadoEm}
                  onChange={set('depositadoEm')}
                  options={usuarios.map(u => ({ value: u.nome, label: u.nome }))}
                  placeholder="Selecione (opcional)"
                />
              </Field>
            </FormRow>

            <FormRow min={150}>
              <Field label="Tipo da classificação">
                <Select
                  value={form.tipoClassificacao || ''}
                  onChange={set('tipoClassificacao')}
                  options={TIPOS_CLASSIFICACAO}
                  placeholder="Selecione (opcional)"
                />
              </Field>
              <Field label="Valor da classificação"
                obrigatorio={!!form.tipoClassificacao}
                hint={form.tipoClassificacao ? 'Obrigatório porque você escolheu um tipo' : undefined}>
                <input
                  value={form.classificacao || ''}
                  onChange={setEv('classificacao')}
                  placeholder="Ex: 78, 18°, 3, Extra"
                  required={!!form.tipoClassificacao}
                />
              </Field>
            </FormRow>

            <FormRow min={140}>
              <Field label="Umidade (%)">
                <input type="number" step="0.1" min="0" max="100" value={form.umidadePct || ''} onChange={setEv('umidadePct')} placeholder="Ex: 14.5" />
              </Field>
              <Field label="Impureza (%)">
                <input type="number" step="0.1" min="0" max="100" value={form.impurezaPct || ''} onChange={setEv('impurezaPct')} placeholder="Ex: 1.2" />
              </Field>
            </FormRow>

            <FormRow>
              <Field label="Placa do caminhão" largura="cheia">
                <input value={form.placaCaminhao} onChange={setEv('placaCaminhao')} placeholder="ABC-1234" />
              </Field>
            </FormRow>

            <label className="check-linha">
              <input
                type="checkbox"
                checked={form.notaEmitida}
                onChange={e => setForm(f => ({ ...f, notaEmitida: e.target.checked }))}
              />
              Nota fiscal emitida
            </label>
          </div>
        )}

        <div className={`actions ${embutido ? 'modal-rodape' : ''}`} style={embutido ? undefined : { marginTop: '0.5rem' }}>
          {onCancel && (
            <button type="button" className="btn-secondary" onClick={onCancel} disabled={salvando}>
              Cancelar
            </button>
          )}
          <button type="submit" className="btn-primary" disabled={salvando}>
            {salvando ? 'Salvando...' : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
});

export default FormCarga;
